<template>
  <div class="autocomplete" v-on-click-outside="onUnfocus"
    :class="{...modifierClasses, 'autocomplete--opened': isOpened}"
    :readonly="$props.readOnly" :disabled="$props.disabled">
    <input class="ccs-input__field" type="text" v-model="text"
      :placeholder="$props.placeholder" :disabled="$props.disabled"
      @focus="onFocus" @input="onInput" :readonly="$props.readOnly">
    <div v-if="isOpened" class="dropdown-list">
      <div class="dropdown-item" v-for="item in $props.items" :key="`${_uid}-${item.prop}`"
        @click="selectItem(item)">
        {{ !!item.displayText ? item.displayText : item.searchText }}
      </div>
      <div v-if="$props.items.length === 0" class="dropdown-item">
        Поиск не дал результатов
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CcsAutocomplete',
  props: {
    value: {
      required: true,
    },
    placeholder: {
      type: String,
      default: '',
    },
    items: {
      type: Array,
      default: () => [],
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    modifiers: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      text: '',
      isOpened: false,
      isFocused: false,
      lastSelectedItem: {},
    };
  },
  computed: {
    parsedModifiers() {
      return this.modifiers.reduce((result, modifier) => {
        const parsedModifier = {};
        parsedModifier[modifier] = true;
        return Object.assign({}, result, parsedModifier);
      }, {});
    },
    modifierClasses() {
      return {
        'autocomplete--minimal': this.parsedModifiers.minimal,
        'autocomplete--active_only': this.parsedModifiers.active_only && !this.isFocused,
      };
    },
    rawVal: {
      set(change) {
        this.$emit('input', change);
      },
      get() {
        return this.$props.value;
      },
    },
    activeItem() {
      return this.$props.items.find(item => item.prop === this.rawVal);
    },
  },
  methods: {
    setLastSelectedItem(propVal=false) {
      if (!propVal) this.lastSelectedItem = this.$props.items.find(item => item.prop === this.rawVal);
      else this.lastSelectedItem = this.$props.items.find(item => item.prop === propVal);
    },
    updateText() {
      if (!this.activeItem) this.text = '';
      else this.text = this.activeItem.displayText || this.activeItem.searchText || '';
    },
    onFocus() {
      if (this.text.length > 0 && this.rawVal === undefined) {
        this.isOpened = true;
      }
      this.isFocused = true;
      this.$emit('focus');
    },
    onUnfocus() {
      if (this.text.length > 0 && this.rawVal !== undefined) {
        this.text = this.lastSelectedItem.displayText || this.lastSelectedItem.searchText;
      }
      this.isOpened = false;
      this.isFocused = false;
      this.$emit('unfocus');
    },
    onInput() {
      if (this.text.length >= 3) {
        this.$emit('change', this.text);
        this.isOpened = true;
      } else {
        this.isOpened = false;
        this.rawVal = undefined;
        this.$emit('select', undefined);
      }
    },
    /** @param {AutocompleteItem} item*/
    selectItem(item) {
      this.setLastSelectedItem(item.prop);
      this.rawVal = item.prop;
      this.text = item.displayText || item.searchText;
      this.isOpened = false;
      this.isFocused = false;
      this.$emit('unfocus');
      this.$emit('select', item.prop);
    },
  },
  watch: {
    rawVal(to) {
      if (to === undefined) {
        this.text = '';
      } else {
        if (to !== null && this.$props.items.length > 0) {
          this.updateText();
          this.setLastSelectedItem();
        }
      }
    },
    items(to) {
      if (!this.isFocused && to && to.length === 1) {
        this.updateText();
        this.setLastSelectedItem();
      }
    }
  },
  mounted() {
    this.updateText();
    this.setLastSelectedItem();
  },
};
</script>

<style lang="scss" scoped>
.autocomplete {
  position: relative;
  flex: 1;
  width: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  background: $neutralLighter;
  font-size: 16px;
  border-radius: 5px;

  .ccs-input__field {
    box-sizing: border-box;
    padding: 11px 40px 11px 15px;
    width: 100%;
    color: #000;
    background: $neutralLighter;
    font-size: 16px;
    border: 1px solid $neutralLighter;
    border-radius: 5px;
    outline: none;
    transition: border     ease-in-out .2s,
                padding    ease-in-out .2s,
                background ease-in-out .2s;

    &:focus {
      background: #fff;
      border: 1px solid $primaryLightest;
    }

    &::placeholder {
      color: $neutralDarker;
    }

  }

  .dropdown {

    &-item {
      height: 30px;
      display: flex;
      justify-content: flex-start;
      align-items: center;
      transition: background ease-in-out .2s, color ease-in-out .2s;

      &--active {
        flex: 1;
        color: #000;

        &:hover {
          cursor: pointer;
        }

      }

      &--placeholder {
        flex: 1;
        color: $neutralDark;

        &:hover {
          cursor: pointer;
        }

      }

    }

    &-list {
      z-index: 100;
      position: absolute;
      top: 42px;
      max-height: 75px;
      width: 100%;
      background: #fff;
      font-size: 16px;
      overflow-y: scroll;
      scrollbar-width: none;
      -ms-overflow-style: none;
      border-radius: 5px;
      box-shadow: 0px 0px 10px -3px #000;

      &::-webkit-scrollbar {
        display: none;
      }

      .dropdown-item {
        padding-left: 15px;

        &:hover {
          color: #fff;
          background: $primary;
          cursor: pointer;
        }

      }

    }

  }

  &--active_only {

    .ccs-input__field {
      padding: 0;
      background: #fff;
      border: 1px solid #fff;
    }

  }

  &--minimal {
    @extend .autocomplete--active_only;

    .ccs-input__field {

      &:focus {
        background: #fff;
        border: 1px solid #fff;
      }

    }

    .dropdown-list {
      top: 25px;
      left: -15px;
    }

  }

  &[readOnly = "readonly"] {

    .ccs-input__field {
      padding: 0;
      background: #fff;
      border: 1px solid #fff;

      &:hover {
        cursor: default;
      }

    }

    &:hover {
      cursor: default;
    }

  }

  &[disabled = "disabled"] {

    .ccs-input__field {
      &:hover {
        cursor: not-allowed;
      }

    }

    &:hover {
      cursor: not-allowed;
    }

  }

}
</style>
